const article = {
  slug: "how-to-win-expired-listings",
  cluster: "agent",
  title: "How to Win Expired Listings: Diagnose Why, Prove a Better Plan",
  description:
    "How to win expired listings: diagnose why the listing failed (price, presentation, promotion), run the expired approach, and prove a better plan with a Listing Review.",
  audience: "Real estate agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "7 min read",
  tldr: "To win expired listings, diagnose why the first attempt failed, sort the cause into price, presentation, or promotion, then bring a specific better plan instead of a generic pitch. Lead with proof: a quick Listing Review that shows the seller exactly what went wrong and what you'd change. Handle the predictable objections, and you convert frustration into a signed agreement.",
  blocks: [
    {
      type: "p",
      spans: [
        "An expired listing is a motivated seller who already wanted to sell and couldn't. That makes them one of the highest-intent leads in real estate — and one of the most skeptical, because the last agent already let them down. You don't win expired listings by being more enthusiastic than the last agent. You win by diagnosing exactly why the listing failed, proving you understand it better than anyone who has called them, and bringing a concrete, different plan to fix it.",
      ],
    },
    {
      type: "callout",
      spans: [
        "The winning move on an expired listing is not 'I'll try harder.' It's 'Here is specifically why your home didn't sell, and here is exactly what I'll do differently.' Diagnosis beats enthusiasm every time.",
      ],
    },
    {
      type: "h2",
      text: "Why do listings expire? The three-bucket taxonomy",
      id: "why-listings-expire",
    },
    {
      type: "p",
      spans: [
        "Almost every expired listing fails for one of three reasons — and usually a combination. Before you call, decide which bucket the listing falls into, because your entire pitch changes depending on the answer. The three buckets are price, presentation, and promotion.",
      ],
    },
    {
      type: "table",
      headers: ["Bucket", "What went wrong", "Tell-tale signs", "What it actually means"],
      rows: [
        [
          "Price",
          "The home was priced above what the market would pay",
          "Many showings but no offers, or steady price cuts that always trailed the market",
          "The home was probably fine — the number was wrong. See [is my house overpriced](/resources/is-my-house-overpriced).",
        ],
        [
          "Presentation",
          "The home showed poorly — photos, description, staging, or condition",
          "Lots of online views but few showings, or showings with no second look",
          "Buyers clicked, then clicked away. The listing didn't earn the visit. See [views but no showings](/resources/views-but-no-showings).",
        ],
        [
          "Promotion",
          "Not enough of the right buyers ever saw it",
          "Low view counts, thin photo set, no marketing plan, listed in a slow window",
          "The home never got its audience. A marketing problem, not a home problem.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "The diagnostic shortcut: where in the funnel did the listing stall? No views means a promotion problem. Views but no showings means a presentation problem. Showings but no offers almost always means a price problem. Learning to read ",
        {
          text: "days on market",
          href: "/resources/days-on-market-explained",
        },
        " and the view-to-showing-to-offer funnel is what separates a credible expired pitch from a generic one.",
      ],
    },
    {
      type: "stat",
      value:
        "No views = promotion. Views but no showings = presentation. Showings but no offers = price.",
      label: "The funnel tells you which bucket the expired listing falls into",
    },
    {
      type: "h2",
      text: "What is the right approach to an expired listing?",
      id: "the-expired-approach",
    },
    {
      type: "p",
      spans: [
        "The seller has likely heard from a dozen agents, all saying roughly the same thing. Your job is to sound like the only one who did homework. The approach in order:",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Lead with empathy, not a pitch. Acknowledge that the process was frustrating and that they did want to sell. Do not bad-mouth the previous agent — it makes you look small and reminds them they chose poorly.",
        ],
        [
          "Diagnose before you prescribe. Pull the old listing, the photos, the price history, and the comps. Identify which bucket (price, presentation, promotion) it stalled in. Specificity is your credibility.",
        ],
        [
          "Bring proof, not promises. Show — don't tell — what you'd do differently. This is where a Listing Review does the heavy lifting (more below).",
        ],
        [
          "Reframe the relist. Sellers fear that relisting just resets the same failure. Explain that a relist with a corrected price, refreshed presentation, and a real marketing plan is a different listing, not the same one. See ",
          {
            text: "how to relist a house",
            href: "/resources/how-to-relist-a-house",
          },
          ".",
        ],
        [
          "Ask for the appointment, not the signature. The goal of the first contact is a sit-down where you walk them through the diagnosis. Treat it like any ",
          {
            text: "listing appointment",
            href: "/resources/how-to-win-the-listing-appointment",
          },
          " — preparation wins it.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Most agents skip step 2 and 3 entirely and jump straight to 'list with me.' That is exactly why the seller's guard is up. When you show up with a real diagnosis, you are no longer one of twelve interchangeable calls — you are the one person who actually looked at their home.",
      ],
    },
    {
      type: "h2",
      text: "How do you prove a better plan instead of just claiming one?",
      id: "prove-a-better-plan",
    },
    {
      type: "p",
      spans: [
        "Every agent claims a better plan. Almost none of them can show one before they're hired. The credibility gap is the whole game on expired listings — and the way to close it is to bring a tangible artifact to the table. That's where a ",
        {
          text: "Listing Review",
          href: "/resources/listing-review-vs-pre-listing-prep",
        },
        " becomes your unfair advantage.",
      ],
    },
    {
      type: "p",
      spans: [
        "A Listing Review takes the home's existing listing and produces an at-a-glance assessment of market competitiveness, listing appeal, and price position, plus an optimized description rewrite, photo-sequencing recommendations, AI photo touch-ups, and a comparative market analysis. In other words, it turns 'trust me, I'd do better' into 'here is the better version, side by side with what failed.'",
      ],
    },
    {
      type: "p",
      spans: [
        "When you sit down with an expired seller, you're no longer pitching — you're presenting evidence. You can put the old description next to a rewritten one, show the old photo order next to a sequence that leads with the strongest room, and lay the asking price against a real ",
        {
          text: "CMA",
          href: "/resources/what-is-a-cma",
        },
        ". The conversation shifts from 'why should I trust you' to 'how soon can we start.'",
      ],
    },
    {
      type: "callout",
      spans: [
        "Bring the artifact, not the adjectives. A seller can't compare two agents' enthusiasm. They can instantly compare a dead listing to a side-by-side, fixed version of it. Make the comparison impossible to ignore.",
      ],
    },
    {
      type: "p",
      spans: [
        "You can generate this proof in minutes before the appointment by running the prior listing through a ",
        {
          text: "Listing Review",
          href: "/get-started",
        },
        ". Walk in with the printed before-and-after, and you've done more visible work than the last agent did in months on market.",
      ],
    },
    {
      type: "h2",
      text: "How do you handle expired-listing objections?",
      id: "objection-table",
    },
    {
      type: "p",
      spans: [
        "Expired sellers come with predictable objections because they've been burned. Don't improvise these — have a calm, specific answer ready for each. The pattern is the same throughout: acknowledge, reframe with diagnosis, point to proof.",
      ],
    },
    {
      type: "table",
      headers: ["Objection", "What they're really saying", "How to respond"],
      rows: [
        [
          '"We\'re just going to wait and relist later"',
          "I'm afraid of repeating the failure",
          '"Waiting only helps if something changes. Let me show you exactly what went wrong this time so the relist is a different listing, not a rerun." Then show the review.',
        ],
        [
          '"All you agents are the same"',
          "Prove you're not",
          "\"Fair — most calls sound identical. Here's the difference: I already pulled your listing and the comps. Can I show you the three specific things I'd change?\"",
        ],
        [
          '"The last agent said the same things you\'re saying"',
          "Words are cheap",
          '"Then let me skip the words. Here\'s the rewritten description and the corrected price next to what was used. Compare them yourself."',
        ],
        [
          '"My home is worth more than the market says"',
          "Don't insult my home",
          '"Your home may be great — the price was the only thing the market rejected. Here are the comps that closed. Let\'s price to actually sell." See [pricing a listing](/resources/how-to-price-a-listing-cma).',
        ],
        [
          '"We had tons of showings and still no offers"',
          "I don't understand why it failed",
          '"Showings without offers is almost always a price signal, not a home problem. Buyers liked it enough to visit — the number stopped them."',
        ],
        [
          '"We barely got any showings at all"',
          "Was it even marketed?",
          '"That points to presentation or reach, not your home. Few clicks means the photos and description didn\'t earn the visit, or the right buyers never saw it."',
        ],
      ],
    },
    {
      type: "h2",
      text: "How do you find and prioritize expired listings?",
      id: "find-and-prioritize",
    },
    {
      type: "p",
      spans: [
        "Sourcing is straightforward — expired and withdrawn listings surface in the MLS, and several services compile owner contact info. The differentiator is not access; it's preparation. Prioritize the expireds where your diagnosis is strongest and your fix is most obvious:",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "Listings that stalled on presentation (weak photos, thin or generic descriptions) — these are the easiest to visibly fix and the most satisfying to demonstrate with a before-and-after.",
        ],
        [
          "Listings with obvious price-trailing behavior (repeated late price cuts) — the diagnosis writes itself, and a clean CMA makes the case.",
        ],
        [
          "Recently expired over slow-but-not-broken listings — fresher frustration, fewer competing agents who've already given up.",
        ],
        [
          "Homes in price bands and neighborhoods you already know well — your local comp knowledge makes the diagnosis sharper and more believable.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Volume is not the goal. Five expired listings where you arrive with a specific, proof-backed plan will beat fifty generic dials. If you're newer to listings and building this muscle, the same diagnose-and-prove method applies — see ",
        {
          text: "how to get listings as a new agent",
          href: "/resources/how-to-get-listings-new-agent",
        },
        ".",
      ],
    },
    {
      type: "callout",
      spans: [
        "The expired-listing formula in one line: diagnose the real reason (price, presentation, or promotion), bring a side-by-side proof of the fix, and answer the burned-seller objections with evidence instead of enthusiasm.",
      ],
    },
  ],
  faqs: [
    {
      question: "What is the best opening line when calling an expired listing?",
      answer: [
        {
          type: "p",
          spans: [
            'Lead with empathy and a hint of diagnosis, not a pitch. Something like: "I know the last few months have been frustrating — you wanted this sold. I pulled your listing and the comps, and I think I can see specifically why it stalled. Could I show you?" This signals you did homework, separates you from the dozen agents saying the same generic things, and earns the appointment instead of triggering the seller\'s defenses.',
          ],
        },
      ],
    },
    {
      question: "Should I criticize the previous agent to win the listing?",
      answer: [
        {
          type: "p",
          spans: [
            "No. Bad-mouthing the previous agent makes you look small and reminds the seller they chose poorly, which puts them on the defensive. Instead, stay neutral about the agent and specific about the listing. Focus on what the market did — price trailing, weak presentation, low reach — and what you'd change. Critique the listing's execution, never the person, and let your diagnosis and proof do the persuading.",
          ],
        },
      ],
    },
    {
      question:
        "How do I know whether an expired listing failed on price, presentation, or promotion?",
      answer: [
        {
          type: "p",
          spans: [
            "Read the funnel. If the listing got almost no online views, it's a promotion or reach problem — the right buyers never saw it. If it got plenty of views but few showings, it's usually a presentation problem — the photos and description didn't earn the visit — though a price that feels high relative to the photos can also stop the tour. If it had steady showings but no offers, it's almost always a price problem — buyers liked the home but the number stopped them. The point where the listing stalled tells you the bucket.",
          ],
        },
      ],
    },
    {
      question: "How does a Listing Review help me win an expired listing?",
      answer: [
        {
          type: "p",
          spans: [
            "It converts your claim of a better plan into visible proof. A Listing Review takes the home's existing (failed) listing and produces an assessment of competitiveness, appeal, and price position, plus a rewritten description, photo-sequencing recommendations, photo touch-ups, and a CMA. You walk into the appointment with a side-by-side of the dead listing and a corrected version, so the seller can compare instead of taking your word. You can generate it in minutes before the meeting at /get-started.",
          ],
        },
      ],
    },
    {
      question: "Is it worth relisting an expired home, or will it just fail again?",
      answer: [
        {
          type: "p",
          spans: [
            "It's worth relisting only if something actually changes. A relist with the same price, same photos, and same marketing is a rerun and will likely fail again. But a relist with a corrected price, refreshed presentation, and a real marketing plan is effectively a different listing. The key is identifying which of the three failure buckets applied last time and fixing that specifically before the home goes back on the market.",
          ],
        },
      ],
    },
  ],
  related: [
    "is-my-house-overpriced",
    "views-but-no-showings",
    "days-on-market-explained",
    "how-to-relist-a-house",
  ],
};

export default article;
